import { useApp } from '@/hooks/useApp'
import spaceStyle from '@/styles/space'
import { memo } from 'react'
import Autosuggest, { type Theme } from 'react-autosuggest'
import './AutoSuggestInputComponent.css'

interface AutoSuggestInputComponentProps<T> {
  suggestions: T[]
  onSuggestionsClearRequested: () => void
  onSuggestionsFetchRequested: Autosuggest.SuggestionsFetchRequested
  onSuggestionSelected?: Autosuggest.OnSuggestionSelected<T>
  getSuggestionValue: (suggestion: T) => any
  renderInputComponent?: Autosuggest.RenderInputComponent
  renderSuggestion: (suggestion: T) => JSX.Element
  inputProps: {
    placeholder: string
    value: any
    onChange: (
      _: React.FormEvent<HTMLElement>,
      { newValue, method }: Autosuggest.ChangeEvent
    ) => void
  }
  theme?: Autosuggest.Theme
  highlightFirstSuggestion?: boolean
  isError?: boolean
}

const AutoSuggestInputComponent = <T,>({
  suggestions,
  onSuggestionsClearRequested,
  onSuggestionsFetchRequested,
  onSuggestionSelected,
  getSuggestionValue,
  renderInputComponent,
  renderSuggestion,
  inputProps,
  theme,
  highlightFirstSuggestion,
  isError = false
}: AutoSuggestInputComponentProps<T>): JSX.Element => {
  const app = useApp()

  const inputBorder = isError
    ? app.theme.errorMainColor.color
    : app.theme.frameColor.color

  const defaultTheme: Theme = {
    container: {
      position: 'relative'
    },
    input: {
      width: '100%',
      height: 36,
      padding: `${spaceStyle.NONE} ${spaceStyle.M}`,
      fontWeight: 300,
      fontSize: 14,
      border: `1px solid ${inputBorder ?? 'rgba(0, 0, 0, 0.12)'}`,
      borderTopLeftRadius: 4,
      borderTopRightRadius: 4,
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4
    },
    inputFocused: {},
    inputOpen: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    },
    suggestionsContainer: {
      display: 'none'
    },
    suggestionsContainerOpen: {
      display: 'block',
      position: 'absolute',
      top: 36,
      width: '100%',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      backgroundColor: `${app.theme.whiteColor.color ?? '#fff'}`,
      fontWeight: 300,
      fontSize: 14,
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      maxHeight: '200px',
      overflowY: 'auto',
      zIndex: 2
    },
    suggestionsList: {
      margin: 0,
      padding: 0,
      listStyleType: 'none'
    },
    suggestion: {
      cursor: 'pointer',
      padding: `${spaceStyle.NONE} ${spaceStyle.M}`,
      minHeight: '36px',
      fontSize: 14,
      fontWeight: 300
    },
    suggestionHighlighted: {
      backgroundColor: app.theme.frameColor.color ?? 'rgba(0, 0, 0, 0.12)',
      fontSize: 14,
      fontWeight: 300
    }
  }

  return (
    <div className="auto-suggest">
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionSelected={onSuggestionSelected}
        getSuggestionValue={getSuggestionValue}
        renderInputComponent={renderInputComponent}
        renderSuggestion={renderSuggestion}
        inputProps={inputProps}
        theme={theme ?? defaultTheme}
        highlightFirstSuggestion={highlightFirstSuggestion}
      />
    </div>
  )
}

export default memo(
  AutoSuggestInputComponent
) as typeof AutoSuggestInputComponent
